import { Config } from '@chegg-wt/core/config'
import RegexEscape from 'regex-escape'
import { trackWithoutThrow } from './errors'
import { isWindowDefined } from './isWindowDefined'

const VALID_POSSIBLY_URL_ENCODED_EMAIL_REGEX = /[\w+\-.]+(@|%40)[a-z\d\-.]+\.[a-z]+/i
export const stripEmail = (url: string): string => {
  if (VALID_POSSIBLY_URL_ENCODED_EMAIL_REGEX.test(url)) {
    return url.replace(VALID_POSSIBLY_URL_ENCODED_EMAIL_REGEX, '')
  }
  return url
}

const patternTest = (pattern: RegExp) => (subject: string) => pattern.test(subject)
export const urlStartsWithHTTPProtocol = patternTest(/^https?:\/\//)

export const addQueryString = (url: string, query: { [key: string]: string }) => {
  const queryString = new URLSearchParams(
    Object.entries(query)
      /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
      .filter(([key, value]) => !!value)
      .map(([key, value]) => `${key}=${value}`)
      .join('&')
  ).toString()

  if (!queryString) return url
  if (url.includes('?')) return `${url}&${queryString}`
  return `${url}?${queryString}`
}

export const publicUrl = (path: string): string =>
  Config.assetPrefix ? Config.assetPrefix + path : path

export const publicAssetUrl = (assetFilename: string) =>
  publicUrl(`/static/images/compressed/${assetFilename}`)

export const basePathUrl = (path: string): string =>
  Config.basePath ? Config.basePath + path : path

export const getSegmentFn = ({ segmentRoutes, matchSegment, paramName }) => (
  router,
  query
) => {
  const { route } = router

  // Note this is NOT query object from next router
  return query && segmentRoutes.includes(route)
    ? matchSegment(query).params[paramName]
    : query
}

export const getIsExactFn = ({ segmentRoutes, matchSegment }) => (route, query) =>
  query && segmentRoutes.includes(route) ? matchSegment(query).isExact : true

export const getSpecialSegmentFn = ({ segmentRoutes, matchSegment, paramName }) => (
  router
) => {
  const { route, asPath } = router
  return segmentRoutes.includes(route) || segmentRoutes.includes(asPath)
    ? matchSegment(asPath).params[paramName]
    : undefined
}

export const isCheggFreeURL = (pathname: string) =>
  !!(pathname.match('/citations$') || pathname.match('/citations/free/'))

export const trimSlashAndQuery = (path: string) => {
  const [pathWithoutQuery] = path.split('?')
  const trailingSlashButNotIndex = /\b(?!^)\/$/
  return pathWithoutQuery.replace(trailingSlashButNotIndex, '')
}

export const trimOrigin = (path: string) => {
  if (!isWindowDefined) return path

  const domain = window.location.origin
  return path.replace(domain, '')
}

export const isAdsFreeURL = (pathname: string) => /^(\/dashboard)/.test(pathname)

export const isSameOrigin = (url: string) =>
  isWindowDefined &&
  new RegExp(`^${RegexEscape(window.location.origin)}(\\/|$)`).test(url)

export const decodeQueryParam = (param: string): string => {
  try {
    return decodeURIComponent(param.replace(/\+/g, ' '))
  } catch (e) {
    trackWithoutThrow(e)
    return ''
  }
}

export const isNonCanonicalRoute = (route: string): boolean => {
  const nonCanonicalPathsPattern = new RegExp(
    `/(.*)/((custom|new|eval|confirm|edit|search)(/?)$)`
  )
  return nonCanonicalPathsPattern.test(route)
}
